import { APP_TYPE, AppType } from "../app.type";
import { updateWarehouse } from "../api.service";
import { Inject, Injectable } from "@angular/core";
import { WarehouseGroupServiceType } from "./warehouse-group.service.type";
import { WarehouseGroup } from "../../../../shared/models/warehouseGroup";
import { hasChanges } from "../utils/app.utils";
import { getAllWarehouseGroupDocs } from "../api.service";
import { StringUtils } from "../../../../shared/utils/string.utils";

@Injectable()
export class WarehouseGroupService implements WarehouseGroupServiceType {
  constructor(@Inject(APP_TYPE) private app: AppType) {}
  warehouseGroups: WarehouseGroup[] = [];
  cleanWarehouseDocs: WarehouseGroup[] = [];

  cleanWarehouse = new WarehouseGroup();
  selectedWarehouse = new WarehouseGroup();

  async getAllWarehouseGroupDocs() {
    this.warehouseGroups = await getAllWarehouseGroupDocs();
    this.cleanWarehouseDocs = this.warehouseGroups;
  }

  modelHasChanges(): boolean {
    return hasChanges(this.cleanWarehouse, this.selectedWarehouse);
  }

  edit(doc: WarehouseGroup) {
    if (doc == null) {
      return;
    }
    if (doc["_id"] != null) {
      this.app.unlockedId = doc["_id"];
    }
    this.selectedWarehouse = { ...doc };
    this.cleanWarehouse = { ...doc };
  }

  async save(currentWarehouse: WarehouseGroup) {
    this.app.spinner.showSpinner();
    await updateWarehouse(currentWarehouse);

    // Get the warehouse documents details updated
    await this.getAllWarehouseGroupDocs();

    // Reset model
    this.selectedWarehouse = new WarehouseGroup();
    this.app.unlockedId = null;
    this.app.spinner.hideSpinner();
  }

  async generateWarehouseGroup(docs: WarehouseGroup[]) {
    this.warehouseGroups = await getAllWarehouseGroupDocs();
    const warehouseGroupDocs: WarehouseGroup[] = [];
    this.app.import.invalidValues = [];

    for (let i = 0; i < docs.length; i++) {
      let warehouseGroup = new WarehouseGroup();
      const existingDoc = this.checkIfWarehouseDocExists(
        this.warehouseGroups,
        docs[i]
      );

      if (existingDoc !== undefined) {
        warehouseGroup = this.updateExistingWarehouseDocs(docs[i], existingDoc);
      } else {
        this.app.import.props.forEach((field) => {
          warehouseGroup[field.key] = docs[i][field.key];
        });
      }

      warehouseGroup = this.setMissingFieldsFromImport(docs[i], warehouseGroup);
      warehouseGroup.type = "warehouseGroup";
      warehouseGroup = this.validateFieldsFromImport(warehouseGroup);

      if (warehouseGroup.invalidValue) {
        warehouseGroupDocs.unshift(warehouseGroup);
        this.app.import.invalidValues.push(warehouseGroup.warehouseGroupId);
      } else {
        warehouseGroupDocs.push(warehouseGroup);
      }
    }

    docs = warehouseGroupDocs;
    this.app.import.docs = docs;
    (docs as []).forEach((doc: any) => {
      this.app.import.selected.add(doc.warehouseGroupId);
    });

    this.app.import.checkForInvalidValues(docs);

    this.app.import.stepper = "reviewList";
    this.app.import.step = "third";
    return this.app.import.docs;
  }

  checkIfWarehouseDocExists(data: any, doc: any) {
    const dataDoc = data.find(
      (d: any) => d.warehouseGroupId === doc.warehouseGroupId
    );
    return dataDoc;
  }

  updateExistingWarehouseDocs(
    doc: WarehouseGroup,
    existingDoc: WarehouseGroup
  ) {
    Object.keys(doc).forEach((key) => {
      if (key !== "id") {
        existingDoc[key] = doc[key];
      }
    });
    return existingDoc;
  }

  setMissingFieldsFromImport(
    importedDoc: WarehouseGroup,
    existingDoc: WarehouseGroup
  ) {
    this.app.import.props.forEach((field) => {
      importedDoc[field.key] = StringUtils.isNullOrEmpty(importedDoc[field.key])
        ? existingDoc[field.key]
        : importedDoc[field.key];

      if (field.key === "warehouseImpact") {
        existingDoc["warehouseImpact"] = !StringUtils.isNullOrEmpty(
          importedDoc["warehouseImpact"]
        )
          ? importedDoc["warehouseImpact"]
          : "unknown";
      }

      if (field.key === "obsolescenceManagement") {
        // the Excel value is still text here, it is converted to a boolean in validateFieldsFromImport
        existingDoc["obsolescenceManagement"] =
          String(importedDoc["obsolescenceManagement"] ?? "") !== ""
            ? importedDoc["obsolescenceManagement"]
            : false;
      }
    });

    return existingDoc;
  }

  validateFieldsFromImport(doc: WarehouseGroup) {
    const validProactiveValues = ["true", "false"];
    const validWarehouseImpactValues = ["low", "medium", "high", "unknown"];
    // Excel values can come as: TRUE, "true", "True"
    const proactiveValue = String(doc.obsolescenceManagement)
      .trim()
      .toLowerCase();

    if (
      !validProactiveValues.includes(proactiveValue) ||
      !validWarehouseImpactValues.includes(doc.warehouseImpact)
    ) {
      this.app.hasError = true;
      this.app.errorText = this.app.text.warehouseGroup.invalidValues;
      doc.invalidValue = true;
    }

    // the Proactive value is saved as a boolean
    if (validProactiveValues.includes(proactiveValue)) {
      doc.obsolescenceManagement = proactiveValue === "true";
    }

    return doc;
  }
}
